import React from 'react';
import {Button} from '@forecast-it/design-system';
import {useId} from '../../../../shared/hooks/useId';
import {VisuallyHidden} from './VisuallyHidden';

interface Props extends React.ComponentProps<typeof Button> {
	/** The control's accessible name. */
	label: string;
}

/**
 * A design-system `Button` with a dependable accessible name.
 *
 * The `Button` builds its own `aria-label` by stringifying its children and
 * applies it after the caller's props, so a caller-supplied `aria-label` is
 * always discarded. Any button whose children are not plain text therefore ends
 * up named after them: an icon child stringifies to `[object Object]`, and a
 * `cond && <El />` child that is false stringifies to `false`, giving names like
 * `[object Object],Edit`.
 *
 * This renders the name into a visually hidden element and points
 * `aria-labelledby` at it. That outranks `aria-label` in the accessible-name
 * algorithm, so the name survives whatever the Button derives.
 *
 * Reach for it whenever a button's children are not plain text. A button whose
 * only child is its label needs nothing — the derived name is already right.
 * Composes with `styled()`: `styled(LabelledButton)` forwards `className`
 * through to the Button as before.
 */
export function LabelledButton({label, children, ...rest}: Props) {
	const labelId = useId();
	return (
		<Button {...rest} aria-labelledby={labelId}>
			{children}
			<VisuallyHidden id={labelId}>{label}</VisuallyHidden>
		</Button>
	);
}
